import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material';
import { Close, Add, CloudUpload, Delete, CheckCircle } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { stopWheelNudge } from '../utils/numberInput';
import { useAuth } from '../contexts/AuthContext';
import EventShareLinks from './EventShareLinks';
import { isStudioScopedRole } from '../utils/roles';
import { useMyLocations, branchLabel, locationsWithRole } from '../hooks/useMyLocations';

interface EventFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  event?: any; // If provided, it's edit mode
  duplicateFrom?: any; // If provided (and no `event`), create mode pre-filled from this event as a template
  studioId?: string; // Pre-fill studio info
}

const EventFormDialog: React.FC<EventFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  event,
  duplicateFrom,
  studioId: studioIdProp,
}) => {
  const { admin } = useAuth();
  const isStudio = isStudioScopedRole(admin?.role);
  const isEditMode = Boolean(event);
  const isSuperAdmin = !isStudio;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set after a successful create — switches the dialog to a success view
  // showing the event's share links (website / SS app / whitelabel app).
  const [createdEventId, setCreatedEventId] = useState<string | null>(null);

  // Studio selector state for superadmin create mode
  const [studios, setStudios] = useState<any[]>([]);
  const [selectedStudioId, setSelectedStudioId] = useState('');

  // ── WHICH BRANCH DOES A NEW EVENT BELONG TO ─────────────────────────────
  //
  // Under "All locations" there is no implied answer, and falling back to
  // `admin.studio._id` created it at whichever branch the owner signed in as
  // — silently, since createEvent pins that id and the server accepts it.
  // Editing is never ambiguous: the event already lives somewhere.
  const { locations, isAllLocations, roleByStudio } = useMyLocations();
  const [branchForNew, setBranchForNew] = useState('');
  const mustPickBranch = isAllLocations && !isEditMode && isStudio;
  // Only where this account may create (manager or above); unfiltered when the
  // server did not send per-location roles.
  const creatableIds = locationsWithRole(roleByStudio, 'manager');
  const branchOptions = creatableIds
    ? locations.filter((l) => creatableIds.includes(String(l._id)))
    : locations;

  const studioId = isEditMode
    ? (event?.studio?._id || event?.studio || studioIdProp)
    : (isStudio
        ? (mustPickBranch ? branchForNew : (admin?.studio?._id || studioIdProp))
        : (selectedStudioId || studioIdProp));
  
  // Form data
  const [formData, setFormData] = useState({
    eventName: '',
    classType: '',
    instructor: [] as string[],
    date: '',
    skillLevel: '',
    price: '',
    slots: '',
    description: '',
    images: [] as string[],
    thingsToBring: [] as string[],
    cancellationPolicy: '',
    payAtEvent: false,
    eventStatus: true,
    address: '',
    city: '',
    state: '',
    pincode: '',
    latitude: '',
    longitude: '',
  });

  // Dropdown options
  const [classTypes, setClassTypes] = useState<any[]>([]);
  const [instructors, setInstructors] = useState<any[]>([]);
  const [studio, setStudio] = useState<any>(null);

  // New item inputs
  const [newImage, setNewImage] = useState('');
  const [newThing, setNewThing] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Fetch studios list for superadmin create mode
  useEffect(() => {
    if (open && isSuperAdmin && !isEditMode && !studioIdProp) {
      apiService.getStudios({}).then((res: any) => {
        setStudios(res.data || res.studios || []);
      }).catch(() => {});
    }
  }, [open, isSuperAdmin, isEditMode, studioIdProp]);

  const fetchStudioData = useCallback(async () => {
    if (!studioId) return;
    try {
      const response = await apiService.getStudioById(studioId!);
      setStudio(response.studio);
      // Pre-fill studio location
      if (response.studio) {
        setFormData(prev => ({
          ...prev,
          address: response.studio.address || '',
          city: response.studio.city || '',
          state: response.studio.state || '',
          pincode: response.studio.pincode || '',
          latitude: response.studio.location?.coordinates?.[1]?.toString() || '',
          longitude: response.studio.location?.coordinates?.[0]?.toString() || '',
        }));
      }
    } catch (err: any) {
      console.error('Failed to fetch studio data:', err);
    }
  }, [studioId]);

  const fetchDropdownData = useCallback(async () => {
    try {
      if (!studioId) {
        apiService.getClassTypes().then((r: any) => setClassTypes(r.classTypes || [])).catch(() => {});
        setInstructors([]);
        return;
      }

      const [classTypesRes, instructorsRes] = await Promise.all([
        apiService.getClassTypes(),
        apiService.getInstructorsByStudio(studioId),
      ]);
      setClassTypes(classTypesRes.classTypes || []);
      setInstructors(instructorsRes.instructors || []);
    } catch (err: any) {
      console.error('Failed to fetch dropdown data:', err);
    }
  }, [studioId]);

  const populateForm = useCallback(() => {
    if (!event) return;
    
    // Backend stores IST time values as UTC (e.g., "2025-11-07T18:30:00.000+00:00" = 18:30 UTC represents 18:30 IST)
    // Extract UTC hours/minutes directly as IST time for datetime-local input
    let localDateTime = '';
    if (event.date) {
      // Create Date object from UTC string
      const utcDate = new Date(event.date);
      
      // Extract UTC date/time components (these represent IST time values)
      const year = utcDate.getUTCFullYear();
      const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getUTCDate()).padStart(2, '0');
      const hours = String(utcDate.getUTCHours()).padStart(2, '0');
      const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
      localDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    setFormData({
      eventName: event.eventName || '',
      classType: event.classType?._id || event.classType || '',
      instructor: event.instructor?.map((i: any) => i._id || i) || [],
      date: localDateTime,
      skillLevel: event.skillLevel || '',
      price: event.price?.toString() || '',
      slots: event.slots?.toString() || '',
      description: event.description || '',
      images: event.images || [],
      thingsToBring: event.thingsToBring || [],
      cancellationPolicy: event.cancellationPolicy || '',
      payAtEvent: event.payAtEvent || false,
      eventStatus: event.eventStatus !== undefined ? event.eventStatus : true,
      address: event.address || '',
      city: event.city || '',
      state: event.state || '',
      pincode: event.pincode || '',
      latitude: event.location?.coordinates?.[1]?.toString() || '',
      longitude: event.location?.coordinates?.[0]?.toString() || '',
    });
  }, [event]);

  // Pre-fill the (create-mode) form from an existing event used as a template.
  // Copies every field EXCEPT identity, forcing eventStatus=false so the
  // duplicate is never created live. The owner reviews/edits, then saves as new.
  const populateFromTemplate = useCallback(() => {
    if (!duplicateFrom) return;

    let localDateTime = '';
    if (duplicateFrom.date) {
      const utcDate = new Date(duplicateFrom.date);
      const year = utcDate.getUTCFullYear();
      const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getUTCDate()).padStart(2, '0');
      const hours = String(utcDate.getUTCHours()).padStart(2, '0');
      const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
      localDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    setFormData({
      eventName: duplicateFrom.eventName || '',
      classType: duplicateFrom.classType?._id || duplicateFrom.classType || '',
      instructor: duplicateFrom.instructor?.map((i: any) => i._id || i) || [],
      date: localDateTime,
      skillLevel: duplicateFrom.skillLevel || '',
      price: duplicateFrom.price?.toString() || '',
      slots: duplicateFrom.slots?.toString() || '',
      description: duplicateFrom.description || '',
      images: duplicateFrom.images || [],
      thingsToBring: duplicateFrom.thingsToBring || [],
      cancellationPolicy: duplicateFrom.cancellationPolicy || '',
      payAtEvent: duplicateFrom.payAtEvent || false,
      eventStatus: false, // duplicates are always created NOT live
      address: duplicateFrom.address || '',
      city: duplicateFrom.city || '',
      state: duplicateFrom.state || '',
      pincode: duplicateFrom.pincode || '',
      latitude: duplicateFrom.location?.coordinates?.[1]?.toString() || '',
      longitude: duplicateFrom.location?.coordinates?.[0]?.toString() || '',
    });
  }, [duplicateFrom]);

  useEffect(() => {
    if (open) {
      // For superadmin, a duplicate must load dropdowns/location for the source
      // event's studio — seed the studio selector before fetching.
      if (duplicateFrom && !event && isSuperAdmin) {
        setSelectedStudioId(duplicateFrom.studio?._id || duplicateFrom.studio || '');
      }
      fetchDropdownData();
      if (studioId) fetchStudioData();
      if (event) populateForm();
      else if (duplicateFrom) populateFromTemplate();
      if (!isEditMode && !duplicateFrom) setSelectedStudioId('');
      setCreatedEventId(null);
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event, duplicateFrom]);

  // Re-fetch when superadmin selects a studio
  useEffect(() => {
    if (open && selectedStudioId) {
      fetchDropdownData();
      fetchStudioData();
    }
  }, [selectedStudioId, open, fetchDropdownData, fetchStudioData]);

  // A fresh create starts with no location chosen (BranchSelect's rule).
  useEffect(() => {
    if (open) setBranchForNew('');
  }, [open]);

  // Under "All locations": the chosen location decides whose instructors and
  // address the form offers. Reload both, pinned to it (fetchDropdownData /
  // fetchStudioData follow `studioId`, which follows branchForNew).
  useEffect(() => {
    if (open && mustPickBranch && branchForNew) {
      fetchDropdownData();
      fetchStudioData();
    }
  }, [branchForNew, open, mustPickBranch, fetchDropdownData, fetchStudioData]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddImage = () => {
    if (newImage.trim()) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImage.trim()],
      }));
      setNewImage('');
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Validate all files
    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError(`File "${file.name}" is not an image file`);
        event.target.value = '';
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(`Image "${file.name}" size should be less than 5MB`);
        event.target.value = '';
        return;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    try {
      setUploadingImage(true);
      setError(null);

      // Upload files to S3 (admin endpoint supports multiple files)
      const response = await apiService.uploadFiles(validFiles, 'events');
      const imageUrls = response.fileLinks || [];

      // Add uploaded image URLs to images array
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...imageUrls],
      }));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to upload images');
    } finally {
      setUploadingImage(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleAddThing = () => {
    if (newThing.trim()) {
      setFormData(prev => ({
        ...prev,
        thingsToBring: [...prev.thingsToBring, newThing.trim()],
      }));
      setNewThing('');
    }
  };

  const handleRemoveThing = (index: number) => {
    setFormData(prev => ({
      ...prev,
      thingsToBring: prev.thingsToBring.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const payload: any = {
        eventName: formData.eventName,
        classType: formData.classType,
        instructor: formData.instructor,
        date: (() => {
          // Send IST time directly to backend - store IST time value as-is
          // User selects IST time (e.g., 6:30 PM IST = 18:30)
          // We send it as UTC format but with IST time values
          // MongoDB will store it as UTC, but the time value (18:30) will be preserved
          // Example: User selects 6:30 PM -> Send "2025-11-07T18:30:00.000Z" -> MongoDB stores 18:30 UTC
          if (!formData.date) throw new Error('Date is required');
          const [datePart, timePart] = formData.date.split('T');
          if (!datePart || !timePart) {
            throw new Error('Invalid date format. Expected YYYY-MM-DDTHH:mm');
          }
          const [year, month, day] = datePart.split('-').map(Number);
          const [hours, minutes] = timePart.split(':').map(Number);
          
          // Validate parsed values
          if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hours) || isNaN(minutes)) {
            throw new Error('Invalid date/time values');
          }
          
          // Send IST time value directly (treating it as UTC for storage)
          // This preserves the IST time value (18:30) in the database
          // Format: YYYY-MM-DDTHH:mm:ss.sss+00:00 (IST time values, but formatted as UTC)
          const istDateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00.000+00:00`;
          
          return istDateString;
        })(),
        skillLevel: formData.skillLevel,
        price: parseFloat(formData.price),
        slots: parseInt(formData.slots),
        description: formData.description,
        images: formData.images,
        thingsToBring: formData.thingsToBring,
        cancellationPolicy: formData.cancellationPolicy,
        payAtEvent: formData.payAtEvent,
        eventStatus: formData.eventStatus,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
      };

      if (isEditMode) {
        payload.eventId = event._id;
        // Pinned to the event's own location — under "All locations" the
        // switcher sends __all__, which the server refuses for writes.
        await apiService.updateEvent(payload, studioId);
        onSuccess();
        onClose();
      } else {
        if (!studioId) {
          throw new Error(
            mustPickBranch
              ? 'Choose which location this event belongs to'
              : 'Studio ID is required to create an event',
          );
        }
        const res: any = await apiService.createEvent(studioId, payload);
        onSuccess();
        const newId = res?.event?._id;
        if (newId) {
          // Keep the dialog open and show the share links for the new event.
          setCreatedEventId(newId);
        } else {
          onClose();
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Success view after create: confirmation + share links for every platform.
  if (createdEventId) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircle color="success" />
              <Typography variant="h6">Event Created</Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>{formData.eventName}</strong> was created successfully. Share it using the links below.
          </Alert>
          <EventShareLinks eventId={createdEventId} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isEditMode ? 'Edit Event' : duplicateFrom ? 'Duplicate Event' : 'Create Event'}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Location picker — a brand owner creating while viewing "All". */}
        {mustPickBranch && (
          <FormControl fullWidth size="small" required sx={{ mb: 2 }}>
            <InputLabel>Location *</InputLabel>
            <Select
              value={branchForNew}
              label="Location *"
              onChange={(e) => {
                setBranchForNew(e.target.value);
                // Instructors picked from the previous location's roster are
                // not instructors at this one.
                setFormData((prev) => ({ ...prev, instructor: [] }));
              }}
            >
              {branchOptions.map((l) => (
                <MenuItem key={l._id} value={l._id}>
                  {branchLabel(l)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {studio && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Event will be created for: <strong>{studio.studioName}</strong>
          </Alert>
        )}

        {/* Share links — always visible when editing an existing event */}
        {isEditMode && event?._id && (
          <Box mb={2}>
            <EventShareLinks eventId={event._id} />
          </Box>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Studio selector — superadmin create mode only */}
          {isSuperAdmin && !isEditMode && !studioIdProp && (
            <FormControl fullWidth size="small" required>
              <InputLabel>Studio *</InputLabel>
              <Select
                value={selectedStudioId}
                label="Studio *"
                onChange={(e) => setSelectedStudioId(e.target.value)}
              >
                {studios.map((s: any) => (
                  <MenuItem key={s._id} value={s._id}>{s.studioName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Event Name */}
          <TextField
            label="Event Name *"
            value={formData.eventName}
            onChange={(e) => handleChange('eventName', e.target.value)}
            fullWidth
            size="small"
          />

          {/* Class Type */}
          <FormControl fullWidth size="small">
            <InputLabel>Class Type *</InputLabel>
            <Select
              value={formData.classType}
              onChange={(e) => handleChange('classType', e.target.value)}
              label="Class Type *"
            >
              {classTypes.map((ct) => (
                <MenuItem key={ct._id} value={ct._id}>
                  {ct.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Instructors */}
          <FormControl fullWidth size="small">
            <InputLabel>Instructors</InputLabel>
            <Select
              multiple
              value={formData.instructor}
              onChange={(e) => handleChange('instructor', e.target.value)}
              input={<OutlinedInput label="Instructors" />}
              renderValue={(selected) => (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {(selected as string[]).map((value) => {
                    const inst = instructors.find(i => i._id === value);
                    return <Chip key={value} label={inst?.name || value} size="small" />;
                  })}
                </Box>
              )}
            >
              {/* A disabled instructor must never be newly assignable — but anyone
                  already on this event stays visible even if since disabled, so
                  editing an existing event never silently drops them from the list. */}
              {instructors
                .filter((inst: any) => !inst.isDisabled || formData.instructor.includes(inst._id))
                .map((inst) => (
                  <MenuItem key={inst._id} value={inst._id}>
                    {inst.name}
                    {inst.isDisabled ? ' — disabled' : ''}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Date & Time */}
          <TextField
            label="Date & Time *"
            type="datetime-local"
            value={formData.date}
            onChange={(e) => handleChange('date', e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          {/* Skill Level */}
          <FormControl fullWidth size="small">
            <InputLabel>Skill Level *</InputLabel>
            <Select
              value={formData.skillLevel}
              onChange={(e) => handleChange('skillLevel', e.target.value)}
              label="Skill Level *"
            >
              <MenuItem value="Beginner">Beginner</MenuItem>
              <MenuItem value="Intermediate">Intermediate</MenuItem>
              <MenuItem value="Advanced">Advanced</MenuItem>
            </Select>
          </FormControl>

          {/* Price & Slots */}
          <Box display="flex" gap={2}>
            <TextField
              label="Price *"
              type="number"
              value={formData.price}
              onChange={(e) => handleChange('price', e.target.value)}
              onWheel={stopWheelNudge}
              fullWidth
              size="small"
            />
            <TextField
              label="Slots *"
              type="number"
              value={formData.slots}
              onChange={(e) => handleChange('slots', e.target.value)}
              onWheel={stopWheelNudge}
              fullWidth
              size="small"
            />
          </Box>

          {/* Description */}
          <TextField
            label="Description *"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            multiline
            rows={3}
            fullWidth
            size="small"
          />

          {/* Images */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Images</Typography>
            
            {/* File Upload Button */}
            <Box mb={2}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="event-image-upload"
                type="file"
                multiple
                onChange={handleFileSelect}
                disabled={uploadingImage}
              />
              <label htmlFor="event-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  size="small"
                  startIcon={<CloudUpload />}
                  disabled={uploadingImage}
                  sx={{ mb: 1 }}
                >
                  {uploadingImage ? 'Uploading...' : 'Upload Images from Computer'}
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                You can select multiple images at once
              </Typography>
            </Box>

            {/* Manual URL Input */}
            <Box display="flex" gap={1} mb={1}>
              <TextField
                placeholder="Or enter Image URL"
                value={newImage}
                onChange={(e) => setNewImage(e.target.value)}
                size="small"
                fullWidth
              />
              <Button variant="outlined" size="small" onClick={handleAddImage} startIcon={<Add />}>
                Add URL
              </Button>
            </Box>

            {/* Image Preview and List */}
            {formData.images.length > 0 && (
              <Box display="flex" flexDirection="column" gap={1} mt={1}>
                {formData.images.map((img, index) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    p={1}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box
                      component="img"
                      src={img}
                      alt={`Event image ${index + 1}`}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
                        {img.length > 50 ? img.slice(0, 50) + '...' : img}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Things to Bring */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Things to Bring</Typography>
            <Box display="flex" gap={1} mb={1}>
              <TextField
                placeholder="Item"
                value={newThing}
                onChange={(e) => setNewThing(e.target.value)}
                size="small"
                fullWidth
              />
              <Button variant="outlined" size="small" onClick={handleAddThing} startIcon={<Add />}>
                Add
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {formData.thingsToBring.map((thing, index) => (
                <Chip
                  key={index}
                  label={thing}
                  onDelete={() => handleRemoveThing(index)}
                  size="small"
                />
              ))}
            </Box>
          </Box>

          {/* Cancellation Policy */}
          <TextField
            label="Cancellation Policy"
            value={formData.cancellationPolicy}
            onChange={(e) => handleChange('cancellationPolicy', e.target.value)}
            multiline
            rows={2}
            fullWidth
            size="small"
          />

          {/* Location */}
          <TextField
            label="Address *"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            fullWidth
            size="small"
          />
          <Box display="flex" gap={2}>
            <TextField
              label="City *"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="State"
              value={formData.state}
              onChange={(e) => handleChange('state', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Pincode"
              value={formData.pincode}
              onChange={(e) => handleChange('pincode', e.target.value)}
              fullWidth
              size="small"
            />
          </Box>
          <Box display="flex" gap={2}>
            <TextField
              label="Latitude *"
              type="number"
              value={formData.latitude}
              onChange={(e) => handleChange('latitude', e.target.value)}
              onWheel={stopWheelNudge}
              fullWidth
              size="small"
            />
            <TextField
              label="Longitude *"
              type="number"
              value={formData.longitude}
              onChange={(e) => handleChange('longitude', e.target.value)}
              onWheel={stopWheelNudge}
              fullWidth
              size="small"
            />
          </Box>

          {/* Switches */}
          <Box display="flex" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.payAtEvent}
                  onChange={(e) => handleChange('payAtEvent', e.target.checked)}
                />
              }
              label="Pay at Event"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.eventStatus}
                  onChange={(e) => handleChange('eventStatus', e.target.checked)}
                />
              }
              label="Event Active"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventFormDialog;

